import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft } from "baseui/icon";
import classNames from "classnames";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../../components/card/SettingsCard";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import PlusSvg from "../../../../components/svg/PlusSvg";
import { Swiper } from "../../../../components/swiper/Swiper";
import { Table } from "../../../../components/table/Table";
import {
  ITaxRate,
  useDeleteTaxCodeMutation,
  useGetTaxRateQuery,
  useSetDefaultTaxCodeMutation,
} from "../../../../services/BillingService";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { AddTaxCode } from "../components/AddTaxCode";

export const TaxRate = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId;

  const { data, isLoading } = useGetTaxRateQuery(orgId, {
    skip: !orgId,
  });

  const [openAdd, setIsOpenAdd] = useState(false);
  // Edit window (owner 2026-10-22: tax rates are edited only here, invoices only pick a code).
  const [editing, setEditing] = useState<ITaxRate | null>(null);
  const [deleteTaxCode] = useDeleteTaxCodeMutation();
  const [setDefaultTaxCode, { isLoading: isSettingDefault }] =
    useSetDefaultTaxCodeMutation();

  const columns = useMemo<ColumnDef<ITaxRate>[]>(
    () => [
      {
        accessorKey: "taxCodeName",
        header: "Tax Code Name",
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <div
            className={"flex items-center gap-1 font-semibold text-[#1D2C3B]"}>
            <span>{row.original.taxCodeName}</span>
            {row.original.isSyncedWithQBO && (
              <img
                src={
                  "https://cdn.shopify.com/app-store/listing_images/a9755168ba32bb3e7fa95c947d11069e/icon/CPafhOnvuf0CEAE=.png"
                }
                height={"18px"}
                width={"18px"}
                alt={"QBO synced"}
              />
            )}
          </div>
        ),
      },
      {
        id: "taxRates",
        header: "Tax Rates",
        accessorFn: (row) =>
          row.taxRates
            .map((rate) => `${rate.taxName} ${rate.taxRate}%`)
            .join(", "),
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className={"flex max-w-[400px] flex-wrap items-center gap-1 py-1"}>
            {row.original.taxRates.map((rate) => (
              <div
                key={rate.id}
                className={
                  "flex gap-4 rounded-[15px] border border-[#E4E9F2] bg-[#F5F7FA] px-3 py-[6px]"
                }>
                <div className={"text-xs text-[#3F5266]"}>{rate.taxName}</div>
                <div className={"text-xs text-[#3F5266]"}>{rate.taxRate}%</div>
              </div>
            ))}
          </div>
        ),
      },
      {
        id: "totalRate",
        header: "Total Rate",
        accessorFn: (row) =>
          row.taxRates.reduce((sum, item) => sum + item.taxRate, 0),
        sortingFn: "basic",
        cell: ({ getValue }) => (
          <span className={"text-sm font-medium text-[#3F5266]"}>
            {getValue<number>()}%
          </span>
        ),
        meta: {
          className: "text-sm font-medium text-[#3F5266]",
        },
      },
      {
        accessorKey: "isActive",
        header: "Active",
        enableSorting: false,
        cell: ({ row }) => (
          <Swiper
            size={"sm"}
            value={row.original.isActive}
            onChange={(value) => {
              deleteTaxCode({
                isActive: value,
                taxCodeId: row.original.id || "",
                orgId: getLinkedFirm().orgId,
              });
            }}
          />
        ),
      },
      {
        // One default per firm: new invoices start with it (Roadmap 1791).
        // Switching another code on turns the old one off; the default can
        // only be changed, not removed, and must be an active code.
        accessorKey: "isDefault",
        header: "Set as default",
        enableSorting: false,
        cell: ({ row }) => {
          const { isDefault, isActive, id } = row.original;
          const canChange = !isDefault && isActive && !!id && !isSettingDefault;

          return (
            <div
              title={
                isDefault
                  ? "New invoices start with this tax code"
                  : !isActive
                  ? "Only an active tax code can be the default"
                  : "Use this tax code for new invoices"
              }
              className={classNames(!canChange && !isDefault && "opacity-50")}>
              <Swiper
                size={"sm"}
                value={!!isDefault}
                onChange={(value) => {
                  if (!value || !canChange || !orgId) {
                    return;
                  }

                  void setDefaultTaxCode({ orgId, taxCodeId: id });
                }}
              />
            </div>
          );
        },
      },
      {
        id: "edit",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type={"button"}
            onClick={() => setEditing(row.original)}
            className={
              "rounded-md border border-[#DCE2EA] px-3 py-1 text-xs font-semibold text-[#6B4EFF] hover:bg-[#F4F2FF]"
            }>
            Edit
          </button>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Date Created",
        sortingFn: "datetime",
        cell: ({ getValue }) => (
          <span className={"text-sm font-normal text-[#3F5266]"}>
            {moment(getValue<string>()).format("DD MMM YYYY")}
          </span>
        ),
        meta: {
          className: "text-sm font-normal text-[#3F5266]",
        },
      },
    ],
    [deleteTaxCode, isSettingDefault, orgId, setDefaultTaxCode],
  );

  return (
    <>
      <div className={"relative font-Manrope"}>
        <div className={"my-6 flex items-center gap-2"}>
          <SettingsIconButton
            hasShadow={false}
            icon={<ChevronLeft />}
            onClick={() => navigate(-1)}
          />
          <div className={"text-2xl font-semibold"}>Billing Settings</div>
        </div>

        <SettingsCard extraClasses={"p-4 w-full"}>
          <div className={"mb-4 flex justify-between"}>
            <div className={"text-lg font-semibold"}>Tax Rate</div>

            <button
              onClick={() => {
                setIsOpenAdd(true);
              }}
              className={
                "flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-[#6938EF] px-4 text-sm font-medium text-white"
              }>
              <PlusSvg />
              <span>Create Tax Code</span>
            </button>
          </div>

          <div>
            {isLoading ? (
              <LottieLoading />
            ) : (
              <Table
                data={data || []}
                columns={columns}
                initialSorting={[{ id: "taxCodeName", desc: false }]}
              />
            )}
          </div>
        </SettingsCard>
      </div>

      {openAdd && (
        <div
          className={
            "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          }>
          <AddTaxCode setIsOpenAdd={setIsOpenAdd} />
        </div>
      )}

      {editing && (
        <div
          className={
            "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          }>
          <AddTaxCode
            key={editing.id}
            taxCode={editing}
            setIsOpenAdd={(open) => {
              const next = typeof open === "function" ? open(true) : open;
              if (!next) setEditing(null);
            }}
          />
        </div>
      )}
    </>
  );
};
